import React, { useRef, useEffect, useId } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faAngleDown } from "@fortawesome/free-solid-svg-icons";

/**
 * Desktop mega-dropdown wrapper. A controlled disclosure: the parent owns the
 * open state so only one dropdown is open at a time. Opens and closes on a
 * button click; also closes on Escape or when keyboard focus leaves it.
 *
 * @param props.label Button text
 * @param props.menuId Stable identifier passed back to onToggle
 * @param props.isOpen Whether this dropdown's panel is shown
 * @param props.onToggle Called with menuId when the button is clicked
 * @param props.onClose Called when Escape or a focus-out should close the panel
 * @param props.children Panel content
 * @return JSX element
 */
export function NavDropdown({
  label,
  menuId,
  isOpen,
  onToggle,
  onClose,
  children,
}) {
  const panelId = useId();
  const wrapperRef = useRef(null);
  const buttonRef = useRef(null);

  useEffect(() => {
    if (!isOpen) return undefined;
    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        onClose();
        buttonRef.current?.focus();
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, onClose]);

  // Close when keyboard focus leaves the dropdown entirely. A null
  // relatedTarget (a mouse click on non-focusable space) is ignored here so
  // clicks inside the panel don't close it; the document-level outside-click
  // handler in the header covers the mouse case.
  const handleBlur = (event) => {
    if (
      isOpen &&
      event.relatedTarget &&
      wrapperRef.current &&
      !wrapperRef.current.contains(event.relatedTarget)
    ) {
      onClose();
    }
  };

  return (
    <div className="navDropdown" ref={wrapperRef} onBlur={handleBlur}>
      <button
        type="button"
        className="navDropdownButton"
        ref={buttonRef}
        aria-haspopup="true"
        aria-expanded={isOpen}
        aria-controls={panelId}
        onClick={() => onToggle(menuId)}
      >
        {label}
        <FontAwesomeIcon icon={faAngleDown} className="navDropdownChevron" />
      </button>
      <div
        id={panelId}
        className={
          isOpen ? "navDropdownPanel navDropdownPanelOpen" : "navDropdownPanel"
        }
        role="group"
        aria-label={label}
        hidden={!isOpen}
      >
        <div className="navDropdownPanelInner">{children}</div>
      </div>
    </div>
  );
}
